// SPDX-License-Identifier: AGPL-3.0-or-later
// Shared in-app attachment viewer for mail, WhatsApp, Ask, Media and files, and Konvo chat and agent
// attachments. Each source supplies its own authenticated loader; the window is FilePreviewWindow
// and the file itself shows in FileContentSurface, as in the Agents file window.

import {FileContentSurface, useFileContent} from '@app/features/file_preview/FileContentPreview';
import type {PreviewAttachment} from '@app/features/file_preview/FilePreviewKind';
import {FilePreviewWindow} from '@app/features/file_preview/FilePreviewWindow';
import {formatFileSize} from '@app/features/messaging/utils/FileUtils';
import {observer} from 'mobx-react-lite';
import type {ReactNode} from 'react';

export function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function toBlob(base64: string, type: string): Blob {
	const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
	return new Blob([bytes], {type});
}

interface AttachmentPreviewModalProps {
	attachment: PreviewAttachment;
	loadBlob: () => Promise<Blob | null>;
	onClose: () => void;
	/** Shown after the size, e.g. where an agent's file is from and who may open it. */
	note?: ReactNode;
	/** Saves the file when the source has its own download (an agent's machine); else the loaded bytes. */
	onDownload?: () => void;
}

export const AttachmentPreviewModal = observer(
	({attachment, loadBlob, onClose, note, onDownload}: AttachmentPreviewModalProps) => {
		const content = useFileContent(attachment, loadBlob);
		const {loaded, blobUrl} = content;
		const fileName = attachment.name;

		return (
			<FilePreviewWindow
				name={fileName}
				contentType={loaded.content_type}
				subline={loaded.size ? formatFileSize(loaded.size) : null}
				note={note}
				onDownload={onDownload ?? content.download}
				downloadDisabled={!onDownload && !blobUrl}
				onClose={onClose}
				data-flx="konvo-mail.attachment-preview.root"
				flxPrefix="konvo-mail.attachment-preview"
			>
				<FileContentSurface content={content} name={fileName} data-flx="konvo-mail.attachment-preview.body" />
			</FilePreviewWindow>
		);
	},
);
